import { Component, computed, effect, input, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
  AttachmentFolderService,
  AuthUserDto,
  TenantItemDto,
  TenantService,
} from '@artemis-software/wr-api';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import TenantTypeEnum = AuthUserDto.TenantTypeEnum;

interface ContractorFolderControl {
  id: string | undefined;
  allowed: boolean;
}

interface FolderContractorControl {
  id: string | undefined;
  contractors: ContractorFolderControl[];
}

interface FolderContractorFormValue {
  folderContractorControls: FolderContractorControl[];
}

@Component({
  standalone: false,
  selector: 'wr-contractor-attachment-folder-view',
  templateUrl: './contractor-attachment-folder-view.component.html',
  styleUrls: ['./contractor-attachment-folder-view.component.scss'],
})
export class ContractorAttachmentFolderViewComponent {
  readonly externalTenantId = input<string | undefined>(undefined);
  readonly isEditing = input<boolean>(false);

  readonly saving$ = new BehaviorSubject(false);
  readonly selectedFolderId = signal<string | undefined>(undefined);
  readonly formSignal = signal<FormGroup | null>(null);

  private savedFormValue: FolderContractorFormValue | null = null;

  readonly folderPage = signal(0);
  readonly contractorFilter = signal('');
  readonly visibleContractorCount = signal(20);
  readonly pageSize = 10;
  private readonly LOAD_MORE_AMOUNT = 20;

  readonly contractors = asyncComputed(() =>
    this.tenantService.findAll({ type: TenantTypeEnum.Contractor, size: 10000 }),
  );

  readonly externalTenantFolders = asyncComputed(() => {
    const id = this.externalTenantId();
    if (id) return this.attachmentFolderService.findAllByTenantId(id);
    return [];
  }, []);

  readonly contractorAllowedFolders = asyncComputed(() => {
    const id = this.externalTenantId();
    if (id) return this.tenantService.findContractorAttachmentFolders(id);
    return [];
  }, []);

  readonly selectedFolderControl = computed(() => {
    const id = this.selectedFolderId();
    const form = this.formSignal();
    if (!id || !form) return null;
    const controls = form.get('folderContractorControls') as FormArray;
    return (controls?.controls.find(c => (c as FormGroup).getRawValue()?.id === id) as FormGroup) ?? null;
  });

  readonly selectedFolderName = computed(() => {
    const id = this.selectedFolderId();
    return this.externalTenantFolders()?.find(f => f.id === id)?.name ?? null;
  });

  readonly externalTenant = asyncComputed(() => {
    const id = this.externalTenantId();
    if (!id) return undefined;
    return this.tenantService.findById(id);
  }, undefined);

  readonly panelTitle = computed(() => {
    const folder = this.selectedFolderName();
    const tenant = this.externalTenant()?.name;
    return tenant && folder ? `${folder} - ${tenant}` : folder;
  });

  readonly selectedContractorItems = computed(() => {
    const control = this.selectedFolderControl();
    const contractors = this.contractors();
    if (!control || !contractors?.length) return [];
    const contractorsArray = control.get('contractors') as FormArray;
    return contractorsArray.controls.map((ctrl, i) => ({
      control: ctrl as FormGroup,
      contractor: contractors[i] as TenantItemDto,
      formArrayIndex: i,
    }));
  });

  // Sorted once per folder selection: checked first, then alphabetical.
  // Does not re-sort when checkboxes change during editing (stable UX).
  readonly sortedContractorItems = computed(() => {
    const items = this.selectedContractorItems();
    return [...items].sort((a, b) => {
      const aAllowed = a.control.getRawValue().allowed as boolean;
      const bAllowed = b.control.getRawValue().allowed as boolean;
      if (aAllowed !== bAllowed) return aAllowed ? -1 : 1;
      return (a.contractor.name ?? '').localeCompare(b.contractor.name ?? '');
    });
  });

  readonly paginatedFolders = computed(() => {
    const folders = this.externalTenantFolders() ?? [];
    const start = this.folderPage() * this.pageSize;
    return folders.slice(start, start + this.pageSize);
  });

  readonly filteredContractorItems = computed(() => {
    const items = this.sortedContractorItems();
    const filter = this.contractorFilter().toLowerCase().trim();
    if (!filter) return items;
    return items.filter(item => item.contractor.name?.toLowerCase().includes(filter));
  });

  readonly visibleContractorItems = computed(() =>
    this.filteredContractorItems().slice(0, this.visibleContractorCount()),
  );

  get folderPageValue(): number { return this.folderPage(); }
  set folderPageValue(v: number) { this.folderPage.set(v); }

  get contractorFilterValue(): string { return this.contractorFilter(); }
  set contractorFilterValue(v: string) { this.contractorFilter.set(v); }

  constructor(
    private readonly tenantService: TenantService,
    private readonly attachmentFolderService: AttachmentFolderService,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: NbToastrService,
  ) {
    effect(() => {
      const contractors = this.contractors();
      const externalTenantFolders = this.externalTenantFolders();
      const contractorAllowedFolders = this.contractorAllowedFolders();
      if (!contractors || !externalTenantFolders || !contractorAllowedFolders) return;

      const folderContractorControls = externalTenantFolders.map(folder => {
        const contractorControls = contractors.map(contractor => {
          const allowed = contractorAllowedFolders
            .find(c => c.id === contractor.id)
            ?.allowedFolders?.some(af => af.id === folder.id) ?? false;
          return this.formBuilder.group({
            id: this.formBuilder.control<string | undefined>(contractor.id),
            allowed: this.formBuilder.control<boolean>(allowed),
          });
        });
        return this.formBuilder.group({
          id: this.formBuilder.control<string | undefined>(folder.id),
          contractors: this.formBuilder.array(contractorControls),
        });
      });

      const form = this.formBuilder.group({
        folderContractorControls: this.formBuilder.array(folderContractorControls),
      });
      form.disable();

      this.formSignal.set(form);
      this.selectedFolderId.set(undefined);
      this.folderPage.set(0);
    });

    effect(() => {
      const editing = this.isEditing();
      const form = this.formSignal();
      if (!form) return;
      if (editing) {
        this.savedFormValue = form.getRawValue();
        form.enable();
      } else {
        form.disable();
      }
    });

    effect(() => {
      this.selectedFolderId();
      this.contractorFilter.set('');
      this.visibleContractorCount.set(20);
    });

    effect(() => {
      this.contractorFilter();
      this.visibleContractorCount.set(20);
    });
  }

  onContractorListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      const total = this.filteredContractorItems().length;
      const current = this.visibleContractorCount();
      if (current < total) {
        this.visibleContractorCount.set(Math.min(current + this.LOAD_MORE_AMOUNT, total));
      }
    }
  }

  cancel(): void {
    const form = this.formSignal();
    if (!form || !this.savedFormValue) return;
    form.setValue(this.savedFormValue);
  }

  async submit(): Promise<boolean> {
    const externalTenantId = this.externalTenantId();
    const form = this.formSignal();
    const contractors = this.contractors();
    if (!externalTenantId || !form || !contractors) return false;

    try {
      this.saving$.next(true);
      const folders = (form.getRawValue() as FolderContractorFormValue).folderContractorControls;

      const mergeDto = contractors.map((contractor, contractorIdx) => ({
        id: contractor.id,
        allowedAttachmentFolderIds: folders
          .filter(folder => folder.contractors[contractorIdx]?.allowed)
          .map(folder => folder.id)
          .filter((id): id is string => id !== undefined),
      }));

      await firstValueFrom(this.tenantService.mergeContractorAttachmentFolders({
        externalTenantId,
        contractors: mergeDto,
      }));

      this.toastrService.success('Dienstleister Ordner Zugriffe wurden erfolgreich gespeichert', 'Dienstleister Ordner Zugriffe');
      return true;
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Fehler beim Speichern der Dienstleister Ordner Zugriffe.', 'Fehler');
      return false;
    } finally {
      this.saving$.next(false);
    }
  }
}
